import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchPayrollBreakdown, type PayrollBreakdownResponse } from "../services/expensesService";
import { Chip, Notice, Spinner } from "./ui";
import { money, monthLabel } from "../lib/format";
import { ui } from "../theme";

// Desglose de la nómina del periodo (#1424), dentro de la fila «Nómina» de
// «¿En qué se va el dinero?». Responde de dónde sale lo «gastado»:
//   · estimado del padrón: cada colaborador con su sueldo del mes y sus bonos;
//   · pagos ya registrados: recibos, liquidaciones y cargos de estados de cuenta.
// El mes en curso cuenta la nómina completa aunque aún no se pague, por eso a
// principios de mes lo «gastado» puede rebasar al presupuesto. Solo dueños.

interface PersonRow {
  key: string;
  name: string;
  currency: string;
  months: number;
  salary: number;
  bonus: number;
  amount: number;
  amountBase: number | null;
}

const round = (n: number) => Math.round(n * 100) / 100;

export default function PayrollBreakdown({ from, to, rowSpent }: { from: string; to: string; rowSpent: number }) {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const tp = (k: string, o?: Record<string, unknown>) => t(`expensesStatements:budgetExec.payroll.${k}`, o);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [data, setData] = useState<PayrollBreakdownResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    fetchPayrollBreakdown({ from, to })
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(apiErrorMessage(err) || "load"));
    return () => {
      alive = false;
    };
  }, [from, to]);

  // Una fila por colaborador y moneda, sumando los meses del periodo.
  const people = useMemo<PersonRow[]>(() => {
    const map = new Map<string, PersonRow>();
    for (const m of data?.months || []) {
      for (const p of m.estimated) {
        const key = `${p.memberId}|${p.currency}`;
        const row = map.get(key) || { key, name: p.name, currency: p.currency, months: 0, salary: 0, bonus: 0, amount: 0, amountBase: 0 };
        row.months += 1;
        row.salary = round(row.salary + p.salary);
        row.bonus = round(row.bonus + p.bonus);
        row.amount = round(row.amount + p.amount);
        row.amountBase = row.amountBase == null || p.amountBase == null ? null : round(row.amountBase + p.amountBase);
        map.set(key, row);
      }
    }
    return [...map.values()].sort((a, b) => (b.amountBase ?? 0) - (a.amountBase ?? 0) || a.name.localeCompare(b.name));
  }, [data]);
  const registered = useMemo(() => (data?.months || []).flatMap((m) => m.registered), [data]);

  const fmt = (v: number | null | undefined, currency = "MXN") => (v == null ? "—" : money(v, currency, language));
  const multiMonth = (data?.months.length || 0) > 1;
  const current = data?.months.find((m) => m.phase === "current");
  const paidSkipped = (data?.skipped || []).filter((s) => s.reason === "pagado");
  const noSalary = (data?.skipped || []).filter((s) => s.reason === "sin_sueldo");
  const differs = data ? Math.abs(data.totals.total - rowSpent) > 0.5 : false;

  return (
    <div className="space-y-3 py-1" data-testid="payroll-breakdown">
      {error && <Notice tone="warn">{error === "load" ? tp("loadError") : error}</Notice>}
      {!data && !error && <Spinner />}

      {data && (
        <>
          <Notice tone="info">
            <p className="font-medium">{tp("how.title")}</p>
            <p className="mt-0.5">{tp("how.body")}</p>
            {current && current.estimatedTotal > 0 && <p className="mt-0.5">{tp("how.current", { month: monthLabel(current.period, language), amount: fmt(current.estimatedTotal) })}</p>}
          </Notice>

          <div>
            <h4 className={`mb-1 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>
              {tp("estimated.title")} · {fmt(data.totals.estimated)}
            </h4>
            {people.length === 0 ? (
              <p className={`text-xs ${c.muted}`}>{tp("estimated.empty")}</p>
            ) : (
              <table className="w-full text-sm" aria-label={tp("estimated.title")}>
                <thead>
                  <tr className={`text-left text-xs ${c.muted}`}>
                    <th className="py-1 pr-2">{tp("col.person")}</th>
                    {multiMonth && <th className="py-1 pr-2 text-right">{tp("col.months")}</th>}
                    <th className="py-1 pr-2 text-right">{tp("col.salary")}</th>
                    <th className="py-1 pr-2 text-right">{tp("col.bonus")}</th>
                    <th className="py-1 text-right">{tp("col.base")}</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.key} className={`border-t ${c.border} ${c.text}`} data-testid={`payroll-person-${p.key}`}>
                      <td className="py-1.5 pr-2">
                        {p.name}
                        {p.currency !== "MXN" && <Chip className="ml-2 bg-sky-500/10 text-sky-600">{p.currency}</Chip>}
                      </td>
                      {multiMonth && <td className={`py-1.5 pr-2 text-right tabular-nums ${c.muted}`}>{p.months}</td>}
                      <td className="py-1.5 pr-2 text-right tabular-nums">{fmt(p.salary, p.currency)}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{p.bonus ? fmt(p.bonus, p.currency) : "—"}</td>
                      <td className="py-1.5 text-right font-semibold tabular-nums">{fmt(p.amountBase)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h4 className={`mb-1 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>
              {tp("registered.title")} · {fmt(data.totals.registered)}
            </h4>
            {registered.length === 0 ? (
              <p className={`text-xs ${c.muted}`}>{tp("registered.empty")}</p>
            ) : (
              <table className="w-full text-sm" aria-label={tp("registered.title")}>
                <thead>
                  <tr className={`text-left text-xs ${c.muted}`}>
                    <th className="py-1 pr-2">{t("expenses.fields.date")}</th>
                    <th className="py-1 pr-2">{tp("col.concept")}</th>
                    <th className="py-1 pr-2">{tp("col.source")}</th>
                    <th className="py-1 text-right">{tp("col.base")}</th>
                  </tr>
                </thead>
                <tbody>
                  {registered.map((e) => (
                    <tr key={e.id} className={`border-t ${c.border} ${c.text}`} data-testid={`payroll-expense-${e.id}`}>
                      <td className="whitespace-nowrap py-1.5 pr-2 tabular-nums">{e.date}</td>
                      <td className="py-1.5 pr-2">
                        {e.vendor || "—"}
                        {e.description && <span className={`ml-2 text-xs ${c.muted}`}>{e.description}</span>}
                      </td>
                      <td className="py-1.5 pr-2">
                        <Chip className="bg-slate-500/10 text-slate-500">{tp(`source.${e.source === "nomina" ? "payslip" : e.source === "equipo" ? "settlement" : "recorded"}`)}</Chip>
                      </td>
                      <td className="py-1.5 text-right font-semibold tabular-nums">
                        {fmt(e.amountBase)}
                        {e.currency !== "MXN" && <span className={`ml-1 text-xs font-normal ${c.muted}`}>{fmt(e.amount, e.currency)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className={`text-sm font-semibold ${c.text}`} data-testid="payroll-total">
            {tp("total", { estimated: fmt(data.totals.estimated), registered: fmt(data.totals.registered), total: fmt(data.totals.total) })}
          </p>
          {differs && <p className={`text-xs ${c.muted}`}>{tp("differs", { row: fmt(rowSpent) })}</p>}
          {paidSkipped.length > 0 && (
            <p className={`text-xs ${c.muted}`} data-testid="payroll-skipped-paid">
              {tp("skipped.paid", { names: [...new Set(paidSkipped.map((s) => s.name))].join(", ") })}
            </p>
          )}
          {noSalary.length > 0 && (
            <p className="text-xs text-amber-600" data-testid="payroll-skipped-nosalary">
              {tp("skipped.noSalary", { names: noSalary.map((s) => s.name).join(", ") })}
            </p>
          )}
          {data.withoutRate.length > 0 && <Notice tone="warn">{tp("withoutRate", { currencies: data.withoutRate.join(", ") })}</Notice>}
        </>
      )}
    </div>
  );
}
